import { BinderSectionData } from '../hooks/useBinderSectionData';
import {
  APIPersonalAutoApplicationFormVehicle,
  APIBinderData,
  APIQuoteVehicle,
} from 'types/api.types';

export const processListedVehiclesData = (
  binderReportData: APIBinderData,
): BinderSectionData => {
  const applicationData = binderReportData?.application_data;
  const quoteData = binderReportData?.quote_data;
  const applicationVehicles = applicationData?.vehicles || [];
  const quoteVehicles = quoteData?.vehicles || [];

  const processedVehicles = applicationVehicles.map(
    (vehicle: APIPersonalAutoApplicationFormVehicle, index: number) => {
      // Get corresponding quote vehicle data
      // Arrays can be shorter than vehicles when extraction misses a record
      const quoteVehicle: Partial<APIQuoteVehicle> = quoteVehicles[index] || {};
      const insuranceCoverage = applicationData?.insurance_coverages?.[index];
      const drivingRecord =
        applicationData?.rating_information_driving_record?.[index];
      const oabCoverage =
        insuranceCoverage?.optional_increased_accident_benefits;

      // Format Year/Make/Model
      const formatYearMakeModel = () => {
        const parts = [vehicle.model_year, vehicle.make, vehicle.model].filter(
          Boolean,
        );
        return parts.length > 0 ? parts.join(' ') : 'N/A';
      };

      // Format currency
      const formatCurrency = (amount: string) => {
        if (!amount || amount === '0' || amount === '') return 'N/A';
        // Remove commas and format
        const cleanAmount = amount.replace(/,/g, '');
        return `$${cleanAmount}`;
      };
      // Get principal driver from quote vehicle data
      const getPrincipalDriver = () => {
        if (quoteVehicle.drivers && quoteVehicle.drivers.length > 0) {
          const principalDriver = quoteVehicle.drivers.find(
            (driver) => driver.is_principal,
          );
          return principalDriver
            ? principalDriver.name
            : quoteVehicle.drivers[index]?.name || 'N/A';
        }
        return 'N/A';
      };

      // Helper to convert boolean to Yes/No
      const boolToYesNo = (value: boolean | undefined | null) => {
        if (value === true) return 'Yes';
        if (value === false) return 'No';

        return 'N/A';
      };

      // Get coverage from quote vehicle breakdown
      const getCoverage = (coverageType: string) => {
        if (
          quoteVehicle.breakdown &&
          quoteVehicle.breakdown.operators &&
          quoteVehicle.breakdown.operators.principal
        ) {
          const principalCoverages = quoteVehicle.breakdown.operators.principal;
          for (const key of Object.keys(principalCoverages)) {
            if (key.toLowerCase().includes(coverageType.toLowerCase())) {
              const coverage = principalCoverages[key];
              if (coverage.limit) {
                return coverage.limit;
              }
              if (coverage.premium) {
                return coverage.premium;
              }
            }
          }
        }
        return 'No';
      };

      // Check if coverage exists in coverages array
      const hasCoverage = (searchTerm: string) => {
        if (quoteVehicle.coverages && Array.isArray(quoteVehicle.coverages)) {
          return quoteVehicle.coverages.some((coverage: string) =>
            coverage.toLowerCase().includes(searchTerm.toLowerCase()),
          )
            ? 'Yes'
            : 'No';
        }
        return 'N/A';
      };

      return {
        // Row 1 - Basic Info
        yearMakeModel: formatYearMakeModel(),
        vin: vehicle.vin || 'N/A',
        classOfUse: drivingRecord?.auto_class || 'N/A',

        // Row 2 - Business/Principal/Record
        vehicleInBusinessName: 'N/A', // Not available in provided data
        principal: getPrincipalDriver(),
        drivingRecord: drivingRecord?.bi || 'N/A',

        // Row 3 - Address
        garagingAddress: quoteData?.address || 'N/A',

        // Row 4 - Purchase Info
        lienholder: vehicle.lienholder || 'N/A',
        purchaseDate: quoteVehicle.purchase_date,

        purchasePrice: formatCurrency(quoteVehicle.purchase_price),
        minorConviction: boolToYesNo(
          drivingRecord?.conviction_description?.includes('minor'),
        ),

        // Row 5 - Coverage Types
        rideshare: boolToYesNo(vehicle.is_ride_sharing),
        liability:
          insuranceCoverage?.liability?.bodily_injury?.limit != null
            ? insuranceCoverage.liability.bodily_injury.limit + '000'
            : 'N/A',
        specifiedPerils: hasCoverage('Specified Perils'),
        comprehensive: hasCoverage('Comprehensive'),
        collision: hasCoverage('Collision'),
        allPerils: getCoverage('All Perils'),

        // Row 6 - OPCF Coverage
        accident: getCoverage('Accident Benefits'),
        opcf20: getCoverage('#20 '),
        opcf27: getCoverage('#27 '),
        opcf43: getCoverage('#43 '),
        opcf49: getCoverage('#49 '),
        winterTire: boolToYesNo(vehicle.is_winter_tires),

        // Row 7 - OAB (Optional Accident Benefits) - Not available in provided data
        oabMedical: oabCoverage?.medical_rehabilitation?.limit || 'N/A',
        oabIncome: oabCoverage?.income_replacement?.limit || 'N/A',
        oabCaregiver:
          oabCoverage?.caregiver_housekeeping_home_maintenance || 'N/A',
        oabDependent: oabCoverage?.dependant_care || 'N/A',
        oabIndexation: oabCoverage?.indexation_benefit || 'N/A',
        oabTort: getCoverage('#48 '),
      };
    },
  );

  const isValid = processedVehicles.length > 0;
  const message =
    processedVehicles.length > 0
      ? `${processedVehicles.length} vehicle(s) listed`
      : 'No vehicles found';

  return {
    title: 'Listed Vehicles',
    description: 'Complete vehicle information and coverage summary.',
    isValid,
    message,
    data: {
      vehicles: processedVehicles,
      vehicleCount: processedVehicles.length,
    },
  };
};
